import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Notification } from '@/types/notifications'
import * as notificationsApi from '@/api/notifications'
import { useAsyncAction } from '@/composables/useAsyncAction'
import { useListFilters } from '@/composables/useListFilters'

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([])
  const unreadCount = ref(0)
  const totalPages = ref(1)

  const { loading, error, execute } = useAsyncAction()

  const {
    filters,
    setFilters: applyFilters,
    setPage: applyPage,
    buildParams: buildFilterParams,
  } = useListFilters({
    unread: false as boolean,
    page: 1,
  })

  let pollTimer: ReturnType<typeof setInterval> | null = null

  async function fetchUnreadCount() {
    await execute(async () => {
      const { data } = await notificationsApi.getUnreadCount()
      unreadCount.value = data.unread_count
    }, 'Error al obtener notificaciones no leídas').catch(() => {})
  }

  async function fetchNotifications() {
    await execute(async () => {
      const { data } = await notificationsApi.list(buildFilterParams())
      // The API may return { data, meta } or just an array
      if (Array.isArray(data)) {
        notifications.value = data
        totalPages.value = 1
      } else if (data && typeof data === 'object' && 'data' in data) {
        const resp = data as { data: Notification[]; meta?: { total_pages: number } }
        notifications.value = resp.data
        totalPages.value = resp.meta?.total_pages ?? 1
      }
    }, 'Error al cargar notificaciones').catch(() => {})
  }

  async function fetchRecent() {
    await execute(async () => {
      const { data } = await notificationsApi.list({ per_page: 10 })
      if (Array.isArray(data)) {
        notifications.value = data
      } else if (data && typeof data === 'object' && 'data' in data) {
        notifications.value = (data as { data: Notification[] }).data
      }
    }, 'Error al cargar notificaciones recientes').catch(() => {})
  }

  async function markAsRead(id: string) {
    await execute(async () => {
      await notificationsApi.markRead(id)
      const n = notifications.value.find((n) => n.id === id)
      if (n) n.read_at = new Date().toISOString()
      if (unreadCount.value > 0) unreadCount.value--
    }, 'Error al marcar notificación como leída').catch(() => {})
  }

  async function markAllAsRead() {
    await execute(async () => {
      await notificationsApi.markAllRead()
      notifications.value.forEach((n) => {
        if (!n.read_at) n.read_at = new Date().toISOString()
      })
      unreadCount.value = 0
    }, 'Error al marcar todas como leídas').catch(() => {})
  }

  function setPage(newPage: number) {
    applyPage(newPage)
    fetchNotifications()
  }

  function setFilterUnread(value: boolean) {
    applyFilters({ unread: value })
    fetchNotifications()
  }

  function startPolling() {
    stopPolling()
    fetchUnreadCount()
    pollTimer = setInterval(fetchUnreadCount, 30_000)
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  return {
    notifications,
    unreadCount,
    loading,
    error,
    filters,
    totalPages,
    fetchUnreadCount,
    fetchNotifications,
    fetchRecent,
    markAsRead,
    markAllAsRead,
    setPage,
    setFilterUnread,
    startPolling,
    stopPolling,
  }
})
